// AuthProvider — auth suite root.
//
// Owns: Supabase auth state subscription, applyAuth wiring into the api suite,
// query-client priming/eviction of the user profile cache when auth state changes.
//
// Sign-in/out FLOWS live in per-action hooks (use-sign-in-with-otp.ts etc.).
// They consume supabase directly rather than going through the provider — the
// provider only surfaces the resolved session snapshot.

"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { useApi, useApplyAuth } from "@/contexts/api";
import { currentUserKeys } from "@/contexts/current-user/keys";
import {
  getAuthState,
  initializeAuthState,
  subscribeAuthState,
  type AuthState,
} from "@/lib/authState";

export interface AuthSnapshot {
  user: User | null;
  session: Session | null;
  loading: boolean;
}

const AuthContext = createContext<AuthSnapshot | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const applyAuth = useApplyAuth();
  const queryClient = useQueryClient();
  const [authState, setAuthState] = useState<AuthState>(() => getAuthState());

  useEffect(() => {
    void initializeAuthState();
    const unsubscribe = subscribeAuthState((nextState) => {
      setAuthState((prev) => {
        if (
          prev.session === nextState.session &&
          prev.user === nextState.user &&
          prev.loading === nextState.loading
        ) {
          return prev;
        }
        return nextState;
      });
    });
    return unsubscribe;
  }, []);

  const authStateAccessToken = authState.session?.access_token;
  useEffect(() => {
    applyAuth({ accessToken: authStateAccessToken });
  }, [applyAuth, authStateAccessToken]);

  const apiProfile = api.profile;
  const authStateUser = authState.user;
  useEffect(() => {
    if (authStateUser) {
      queryClient.invalidateQueries({ queryKey: currentUserKeys.profile() });
      void queryClient.prefetchQuery({
        queryKey: currentUserKeys.profile(),
        queryFn: () => apiProfile.get(),
      });
    } else {
      queryClient.removeQueries({ queryKey: currentUserKeys.all });
    }
  }, [apiProfile, authStateUser, queryClient]);

  return (
    <AuthContext.Provider value={authState}>{children}</AuthContext.Provider>
  );
}

export function useAuthContext(): AuthSnapshot {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("AuthProvider not mounted");
  return ctx;
}
